/**
 * Maintenance Calendar View
 * Visual calendar showing maintenance jobs by date
 * 
 * Features:
 * - Month/Week/Day/Agenda views
 * - Color-coded by status
 * - Notification badges
 * - Click event to see details
 * - Responsive design
 */

import React, { useMemo, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './MaintenanceCalendar.css';
import {
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Grid,
  IconButton,
  Toolbar,
  ButtonGroup
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Timer as TimerIcon,
  CheckCircle as CompleteIcon,
  SkipNext as SkipIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import JobStatusBadge from './JobStatusBadge';
import NotificationBadges from './NotificationBadges';

const localizer = momentLocalizer(moment);

const useStyles = makeStyles((theme) => ({
  root: {
    height: 'calc(100vh - 350px)',
    minHeight: 500,
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 250px)',
      padding: theme.spacing(1),
    },
  },
  calendar: {
    height: '100%',
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    
    // Event styling - Outlook-inspired
    '& .rbc-event': {
      borderRadius: 4,
      padding: '4px 8px',
      border: 'none',
      fontSize: 13,
      lineHeight: 1.4,
      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
      transition: 'all 0.2s ease',
      overflow: 'hidden',
      '&:hover': {
        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
        transform: 'translateY(-1px)',
      },
    },
    '& .rbc-event-label': {
      display: 'none', // Hide the default time label
    },
    '& .rbc-event-content': {
      whiteSpace: 'normal',
      overflow: 'visible',
    },
    
    // Status-based colors with gradient
    '& .rbc-event-overdue': {
      background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
      color: '#fff',
      fontWeight: 600,
      borderLeft: '4px solid #b71c1c',
    },
    '& .rbc-event-pending': {
      background: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
      color: '#fff',
      borderLeft: '4px solid #424242',
    },
    '& .rbc-event-notified': {
      background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
      color: '#fff',
      borderLeft: '4px solid #0d47a1',
    },
    '& .rbc-event-in_progress': {
      background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
      color: '#fff',
      fontWeight: 600,
      borderLeft: '4px solid #e65100',
    },
    '& .rbc-event-completed': {
      background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
      color: '#fff',
      borderLeft: '4px solid #1b5e20',
    },
    '& .rbc-event-cancelled': {
      background: 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
      color: '#555',
      textDecoration: 'line-through',
      borderLeft: '4px solid #757575',
    },
    
    // Today highlight
    '& .rbc-today': {
      backgroundColor: '#fff8e1',
    },
    
    // Toolbar styling
    '& .rbc-toolbar': {
      padding: '16px 0',
      marginBottom: 16,
      flexWrap: 'wrap',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        '& .rbc-toolbar-label': {
          order: -1,
          marginBottom: 8,
          textAlign: 'center',
          fontSize: 18,
          fontWeight: 600,
        },
      },
    },
    '& .rbc-toolbar button': {
      color: theme.palette.primary.main,
      padding: '8px 16px',
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: '#fff',
      fontWeight: 500,
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.main,
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
      '&:active': {
        transform: 'translateY(0)',
      },
      '&.rbc-active': {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        borderColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
      [theme.breakpoints.down('sm')]: {
        padding: '6px 12px',
        fontSize: 13,
      },
    },
    
    // Week/Day view time slots
    '& .rbc-time-slot': {
      minHeight: 40,
    },
    '& .rbc-timeslot-group': {
      minHeight: 80,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .rbc-time-header-content': {
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
    '& .rbc-day-slot .rbc-time-slot': {
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    
    // Month view
    '& .rbc-month-view': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      overflow: 'hidden',
    },
    '& .rbc-month-row': {
      borderTop: `1px solid ${theme.palette.divider}`,
      minHeight: 80,
      [theme.breakpoints.down('sm')]: {
        minHeight: 60,
      },
    },
    '& .rbc-date-cell': {
      padding: 8,
      textAlign: 'right',
      [theme.breakpoints.down('sm')]: {
        padding: 4,
        fontSize: 12,
      },
    },
    '& .rbc-off-range': {
      color: theme.palette.text.disabled,
    },
    '& .rbc-off-range-bg': {
      backgroundColor: theme.palette.grey[50],
    },
    
    // Header styling
    '& .rbc-header': {
      padding: 12,
      fontWeight: 600,
      fontSize: 14,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.grey[50],
      [theme.breakpoints.down('sm')]: {
        padding: 8,
        fontSize: 12,
      },
    },
    
    // Current time indicator
    '& .rbc-current-time-indicator': {
      backgroundColor: theme.palette.error.main,
      height: 2,
    },
    
    // Agenda view
    '& .rbc-agenda-view': {
      '& table': {
        border: `1px solid ${theme.palette.divider}`,
      },
      '& .rbc-agenda-date-cell': {
        fontWeight: 600,
        padding: 12,
      },
      '& .rbc-agenda-time-cell': {
        padding: 12,
        whiteSpace: 'nowrap',
      },
      '& .rbc-agenda-event-cell': {
        padding: 12,
      },
    },
  },
  
  // Custom event component styles
  eventWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  eventTime: {
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 2,
    opacity: 0.9,
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  eventTitle: {
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
    },
  },
  eventDetails: {
    fontSize: 11,
    opacity: 0.85,
    marginTop: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    '& svg': {
      fontSize: 12,
    },
  },
  detailsDialog: {
    minWidth: 400,
  },
  detailsGrid: {
    marginTop: theme.spacing(2),
  },
  detailRow: {
    marginBottom: theme.spacing(2),
  },
  detailLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  detailValue: {
    fontSize: 14,
  },
  notesBox: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
}));

const MaintenanceCalendar = ({ jobs, onEventClick, onNotifyClient, onCompleteJob, onSkipJob, onCancelJob }) => {
  const classes = useStyles();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [view, setView] = useState('month');

  // Transform jobs into calendar events
  const events = useMemo(() => {
    return jobs.map(job => {
      // Determine event style class
      let eventClass = 'rbc-event-pending';
      if (job.status === 'overdue') eventClass = 'rbc-event-overdue';
      else if (job.status === 'completed') eventClass = 'rbc-event-completed';
      else if (job.status === 'cancelled' || job.status === 'skipped') eventClass = 'rbc-event-cancelled';
      else if (job.status === 'in_progress') eventClass = 'rbc-event-in_progress';
      else if (job.client_notified_at || job.engineer_reminded_at) eventClass = 'rbc-event-notified';

      // Parse date and time
      const scheduledDate = moment(job.scheduled_date);
      const scheduledTime = job.scheduled_time || '09:00:00';
      const [hours, minutes] = scheduledTime.split(':');
      const start = scheduledDate.clone().set({ hours: parseInt(hours), minutes: parseInt(minutes) }).toDate();
      const end = scheduledDate.clone().set({ hours: parseInt(hours), minutes: parseInt(minutes) + (job.expected_downtime_minutes || 30) }).toDate();

      return {
        id: job.id,
        title: job.client_name,
        start,
        end,
        resource: job,
        className: eventClass,
      };
    });
  }, [jobs, view]);

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
    setDetailsOpen(true);
    // Don't call onEventClick - keep dialog in calendar only to avoid double dialogs
  };

  // Handle close details
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedEvent(null);
  };

  // Handle notify client
  const handleNotifyClient = () => {
    if (onNotifyClient && selectedEvent) {
      onNotifyClient(selectedEvent.id);
    }
    handleCloseDetails();
  };

  // Handle complete job
  const handleCompleteJob = () => {
    if (onCompleteJob && selectedEvent) {
      onCompleteJob(selectedEvent.id);
    }
    handleCloseDetails();
  };

  // Handle skip job
  const handleSkipJob = () => {
    if (onSkipJob && selectedEvent) {
      onSkipJob(selectedEvent.id);
    }
    handleCloseDetails();
  };

  // Handle cancel job
  const handleCancelJob = () => {
    if (onCancelJob && selectedEvent) {
      onCancelJob(selectedEvent.id);
    }
    handleCloseDetails();
  };

  // Custom event style
  const eventStyleGetter = (event) => {
    return {
      className: event.className,
    };
  };

  // Custom Event Component for better display
  const CustomEvent = ({ event }) => {
    const job = event.resource;
    const timeStr = moment(event.start).format('h:mm A');
    const duration = job.expected_downtime_minutes || 30;
    
    return (
      <div className={classes.eventWrapper}>
        <div className={classes.eventTime}>{timeStr}</div>
        <div className={classes.eventTitle}>{job.client_name}</div>
        {view !== 'month' && (
          <div className={classes.eventDetails}>
            <TimerIcon />
            <span>{duration}m</span>
            {job.assigned_engineer_name && (
              <>
                <PersonIcon />
                <span>{job.assigned_engineer_name.split(' ')[0]}</span>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Paper className={classes.root} elevation={2}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        view={view}
        onView={setView}
        onSelectEvent={handleSelectEvent}
        eventPropGetter={eventStyleGetter}
        components={{
          event: CustomEvent,
        }}
        className={classes.calendar}
        popup
        views={['month', 'week', 'day', 'agenda']}
        step={30}
        showMultiDayTimes
        defaultDate={new Date()}
        tooltipAccessor={(event) => {
          const job = event.resource;
          return `${job.client_name}\n${moment(event.start).format('h:mm A')} - ${moment(event.end).format('h:mm A')}\nAssigned: ${job.assigned_engineer_name || 'Unassigned'}\nStatus: ${job.status}`;
        }}
      />

      {/* Event Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
        className={classes.detailsDialog}
      >
        {selectedEvent && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedEvent.client_name}</Typography>
                <IconButton size="small" onClick={handleCloseDetails}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <JobStatusBadge
                  status={selectedEvent.status}
                  clientNotified={selectedEvent.client_notified_at}
                  engineerReminded={selectedEvent.engineer_reminded_at}
                  size="medium"
                />
              </Box>

              <Grid container spacing={2} className={classes.detailsGrid}>
                {/* Scheduled Date */}
                <Grid item xs={12} className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    <EventIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Scheduled Date & Time
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {moment(selectedEvent.scheduled_date).format('MMMM D, YYYY')} at {selectedEvent.scheduled_time || '09:00:00'}
                  </Typography>
                </Grid>

                {/* Expected Downtime */}
                <Grid item xs={6} className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    <TimerIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Expected Downtime
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {selectedEvent.expected_downtime_minutes || 30} minutes
                  </Typography>
                </Grid>

                {/* Assigned Engineer */}
                <Grid item xs={6} className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>
                    <PersonIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                    Assigned Engineer
                  </Typography>
                  <Typography className={classes.detailValue}>
                    {selectedEvent.assigned_engineer_name || 'Unassigned'}
                  </Typography>
                </Grid>

                {/* Asset */}
                {selectedEvent.asset_name && (
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>
                      <LocationIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                      Asset
                    </Typography>
                    <Typography className={classes.detailValue}>
                      {selectedEvent.asset_name} ({selectedEvent.asset_type || 'N/A'})
                    </Typography>
                  </Grid>
                )}

                {/* Notification Status */}
                <Grid item xs={12} className={classes.detailRow}>
                  <Typography className={classes.detailLabel}>Notifications</Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <NotificationBadges
                      clientNotified={selectedEvent.client_notified_at}
                      engineerReminded={selectedEvent.engineer_reminded_at}
                      notificationDaysBefore={selectedEvent.notification_days_before}
                      scheduledDate={selectedEvent.scheduled_date}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {selectedEvent.client_notified_at ? 'Client notified' : 'Client not yet notified'} • {' '}
                      {selectedEvent.engineer_reminded_at ? 'Engineer reminded' : 'Engineer not yet reminded'}
                    </Typography>
                  </Box>
                </Grid>

                {/* Instructions */}
                {selectedEvent.instructions && (
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>Instructions</Typography>
                    <Box className={classes.notesBox}>
                      <Typography variant="body2">{selectedEvent.instructions}</Typography>
                    </Box>
                  </Grid>
                )}

                {/* Completion Notes */}
                {selectedEvent.completion_notes && (
                  <Grid item xs={12} className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>Completion Notes</Typography>
                    <Box className={classes.notesBox}>
                      <Typography variant="body2">{selectedEvent.completion_notes}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails} startIcon={<CloseIcon />}>
                Close
              </Button>
              
              {/* Notify Client - Show if not notified and not completed/cancelled */}
              {!selectedEvent.client_notified_at && selectedEvent.status !== 'completed' && selectedEvent.status !== 'cancelled' && selectedEvent.status !== 'skipped' && onNotifyClient && (
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={handleNotifyClient}
                  startIcon={<EmailIcon />}
                >
                  Notify Client
                </Button>
              )}

              {/* Complete Job - Show if pending, in_progress, or overdue */}
              {(selectedEvent.status === 'pending' || selectedEvent.status === 'in_progress' || selectedEvent.status === 'overdue') && onCompleteJob && (
                <Button 
                  variant="contained" 
                  style={{ backgroundColor: '#4caf50', color: '#fff' }}
                  onClick={handleCompleteJob}
                  startIcon={<CompleteIcon />}
                >
                  Mark Complete
                </Button>
              )}

              {/* Skip Job - Show if pending, in_progress, or overdue (for engineers) */}
              {(selectedEvent.status === 'pending' || selectedEvent.status === 'in_progress' || selectedEvent.status === 'overdue') && onSkipJob && (
                <Button 
                  variant="outlined" 
                  onClick={handleSkipJob}
                  startIcon={<SkipIcon />}
                >
                  Skip Job
                </Button>
              )}

              {/* Cancel Job - Show if pending, in_progress, or overdue (for admins) */}
              {(selectedEvent.status === 'pending' || selectedEvent.status === 'in_progress' || selectedEvent.status === 'overdue') && onCancelJob && (
                <Button 
                  variant="outlined" 
                  color="secondary"
                  onClick={handleCancelJob}
                  startIcon={<CancelIcon />}
                >
                  Cancel Job
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default MaintenanceCalendar;
